import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight } from 'autogen/translation-keys/trans-website-imagealttext-icons';
import { Button } from 'components/Button';
import type { buttonVariantKeys } from 'components/Button/shared/buttonVariants';
import Icon from 'components/Icon';
import type { ToTransientProps } from 'types/styled';

export type NavigationButtonProps = Omit<HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'children'> & {
    variant?: Extract<buttonVariantKeys, 'navigation' | 'ghost'>;
    direction: 'prev' | 'next';
    size?: 'S' | 'M' | 'L';
    withBorder?: boolean;
};

type TransientNavigationButtonProps = ToTransientProps<NavigationButtonProps>;

function dimensionFromSize(size: NavigationButtonProps['size']): number {
    switch (size) {
        case 'S':
            return 32;
        case 'L':
            return 48;
        case 'M':
        default:
            return 40;
    }
}

const StyledButton = styled(Button)<Required<Pick<TransientNavigationButtonProps, '$withBorder' | '$size'>>>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ $size }) => dimensionFromSize($size)}px;
    width: ${({ $size }) => dimensionFromSize($size)}px;
    padding: 0;
    border: 1px solid ${({ theme, $withBorder }) => ($withBorder ? theme.ColorBorderSecondary : 'transparent')};
    border-radius: ${({ theme }) => theme.RadiusFull};
    box-shadow: 0 4px 8px 2px rgba(0, 0, 0, 0.1);
`;

function NavigationButton(props: NavigationButtonProps) {
    const { variant = 'navigation', direction, size = 'L', withBorder = false, ...restProps } = props;
    const { t } = useTranslation();

    function label() {
        if (direction === 'prev') {
            return t(ArrowLeft);
        }
        if (direction === 'next') {
            return t(ArrowRight);
        }
    }

    return (
        <StyledButton
            type="button"
            variant={variant}
            $withBorder={withBorder}
            $size={size}
            {...restProps}
            aria-label={label()}
        >
            {direction === 'prev' && <Icon type="ChevronLeft" size={size === 'L' ? 24 : 16} />}
            {direction === 'next' && <Icon type="ChevronRight" size={size === 'L' ? 24 : 16} />}
        </StyledButton>
    );
}

export default NavigationButton;
